"use client";

import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Copy,
  Hourglass,
  Info,
  ShieldAlert,
  X,
  XCircle,
} from "lucide-react";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

/** Summary of the appeal tied to this moderation status, if one was submitted. */
export interface AppealSummary {
  id: string;
  /** Human-friendly reference id, e.g. "APL-3F9K2ABC". */
  referenceId: string;
  /** Raw status from the DB (PENDING / APPROVED / DENIED / ...). */
  status: string;
  submittedAt: string;
  resolvedAt?: string | null;
  resolution?: string | null;
}

interface AccountStatusNotificationProps {
  status: "warning" | "suspended" | "banned";
  reason?: string;
  expiresAt?: Date;
  onDismiss?: () => void;
  /**
   * Optional ContentReportAction id this status is tied to. When provided
   * the "Appeal this action" button links to `/appeal?action=<id>` so the
   * appeal form can prefill the moderation action being appealed.
   */
  moderationActionId?: string | null;
  /**
   * Appeal the user has already submitted for this action. When present the
   * banner shows the appeal's status + reference id instead of the
   * "Appeal this action" button.
   */
  appeal?: AppealSummary | null;
}

function getAppealStatusMeta(rawStatus: string) {
  const s = rawStatus.toLowerCase();
  if (s === "approved" || s === "granted" || s === "accepted" || s === "resolved") {
    return {
      label: "Appeal approved",
      icon: CheckCircle2,
      className: "bg-green-500/10 border-green-500 text-green-700 dark:text-green-400",
      description:
        "Good news — your appeal was approved and this action has been removed from your account. Keep this reference number for your records.",
      // Terminal state: a moderator note in `resolution` is meaningful and may
      // be shown in place of the generic status copy.
      decided: true,
    };
  }
  if (s === "denied" || s === "rejected" || s === "declined") {
    return {
      label: "Appeal denied",
      icon: XCircle,
      className: "bg-red-500/10 border-red-500 text-red-700 dark:text-red-400",
      description:
        "After review, your appeal was denied and this action will remain on your account. Keep this reference number for your records.",
      decided: true,
    };
  }
  if (s === "in_review" || s === "reviewing" || s === "under_review") {
    return {
      label: "Appeal in review",
      icon: Hourglass,
      className: "bg-blue-500/10 border-blue-500 text-blue-700 dark:text-blue-400",
      description:
        "Our team is currently reviewing your appeal. Keep this reference number for your records — decisions are typically made within 1–3 business days.",
      decided: false,
    };
  }
  return {
    label: "Appeal submitted — pending review",
    icon: Hourglass,
    className: "bg-blue-500/10 border-blue-500 text-blue-700 dark:text-blue-400",
    description:
      "Our team is reviewing your appeal. Keep this reference number for your records — decisions are typically made within 1–3 business days.",
    decided: false,
  };
}

/**
 * Matches the generic "pending review" boilerplate. A decided appeal must never
 * render this copy — but legacy/echoed `resolution` values (rows written before
 * the status-aware copy landed) can still contain it, so we guard against it
 * showing as if it were a real moderator note.
 */
const PENDING_BOILERPLATE = /reviewing your appeal|typically made within|1[-–]3 business days/i;

export function AccountStatusNotification({
  status,
  reason = "violation of community guidelines",
  expiresAt,
  onDismiss,
  moderationActionId,
  appeal,
}: AccountStatusNotificationProps) {
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  if (dismissed) return null;

  const handleCopyReference = async () => {
    if (!appeal?.referenceId) return;
    try {
      await navigator.clipboard.writeText(appeal.referenceId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (e.g. insecure context) — fail silently.
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const getStatusColor = () => {
    switch (status) {
      case "warning":
        return "bg-amber-500/10 border-amber-500 text-amber-700 dark:text-amber-400";
      case "suspended":
        return "bg-red-500/10 border-red-500 text-red-700 dark:text-red-400";
      case "banned":
        return "bg-destructive/10 border-destructive text-destructive";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "warning":
        return <AlertTriangle className="h-5 w-5" />;
      case "suspended":
        return <Clock className="h-5 w-5" />;
      case "banned":
        return <ShieldAlert className="h-5 w-5" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "warning":
        return "Account Warning";
      case "suspended":
        return "Account Suspended";
      case "banned":
        return "Account Banned";
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case "warning":
        return `Your account has received a warning for ${reason}. Further violations may result in suspension.`;
      case "suspended":
        return `Your account has been temporarily suspended for ${reason}.${expiresAt ? ` Your suspension will be lifted on ${expiresAt.toLocaleDateString()}.` : ""}`;
      case "banned":
        return `Your account has been permanently banned for ${reason}.`;
    }
  };

  return (
    <Card className={`border ${getStatusColor()} mb-6`}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`p-2 rounded-full ${getStatusColor()}`}>
          {getStatusIcon()}
        </div>
        <div className="flex-1">
          <h3 className="font-medium">{getStatusTitle()}</h3>
          <p className="text-sm mt-1">{getStatusMessage()}</p>
          {appeal ? (
            <AppealStatusBlock
              appeal={appeal}
              copied={copied}
              onCopy={handleCopyReference}
            />
          ) : (
            <div className="flex items-center gap-2 mt-2">
              <Info className="h-4 w-4" />
              <span className="text-xs">
                If you believe this action was taken in error, you can submit an
                appeal.
              </span>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDismiss}
          className="h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardContent>
      <CardFooter className="px-4 pb-4 pt-0 flex justify-end">
        {appeal ? (
          <Button size="sm" variant="outline" asChild>
            <Link href="/account/appeals">View Appeal Status</Link>
          </Button>
        ) : (
          <Button size="sm" variant="outline" asChild>
            <Link href={moderationActionId ? `/appeal?action=${encodeURIComponent(moderationActionId)}` : "/appeal"}>
              Appeal This Action
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

function AppealStatusBlock({
  appeal,
  copied,
  onCopy,
}: {
  appeal: AppealSummary;
  copied: boolean;
  onCopy: () => void;
}) {
  const meta = getAppealStatusMeta(appeal.status);
  const StatusIcon = meta.icon;
  const submitted = new Date(appeal.submittedAt);

  // The status is the single source of truth for the body copy. A moderator's
  // free-text `resolution` note only overrides the generic status copy when the
  // appeal is in a decided (approved/denied) state AND the note isn't just the
  // pending-review boilerplate. This prevents an approved appeal from ever
  // showing the "our team is reviewing…" text — whether because the row's
  // `resolution` still holds that legacy sentence or because it's empty.
  const note = appeal.resolution?.trim();
  const showNote = Boolean(meta.decided && note && !PENDING_BOILERPLATE.test(note));

  return (
    <div className={`mt-3 rounded-lg border px-3 py-2.5 ${meta.className}`}>
      <div className="flex items-center gap-2">
        <StatusIcon className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">{meta.label}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="opacity-70">Reference</span>
          <span className="font-mono font-semibold tracking-wide">
            {appeal.referenceId}
          </span>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded px-1 py-0.5 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Copy reference ID"
          >
            <Copy className="h-3 w-3" />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="opacity-70">Submitted</span>
          <span className="font-medium">
            {submitted.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>
      </div>
      {showNote ? (
        <p className="mt-2 text-xs leading-relaxed opacity-90">
          {note}
        </p>
      ) : (
        <p className="mt-2 text-xs leading-relaxed opacity-80">
          {meta.description}
        </p>
      )}
    </div>
  );
}
